import { useState, useEffect } from "react";
import RTC, { RecordRTCPromisesHandler } from "recordrtc";
import { trackDiagnosticException, trackDiagnosticTrace } from "@utils/diagnostics/appInsightsDiagnostics";

export const useRecorderPermission = (
    enableDiagnostics?: boolean,
    enableAndroidAudioConstraints?: boolean
): [
    MediaStream | undefined,
    (sourceStream?: MediaStream) => RecordRTCPromisesHandler | undefined,
    () => Promise<MediaStream | undefined>
] => {
    const [stream, setStream] = useState<MediaStream>();

    const getAndroidLikeBrowser = (): boolean => {
        const userAgent = navigator.userAgent ?? "";
        const platform = navigator.platform ?? "";
        const userAgentDataPlatform = (navigator as any).userAgentData?.platform ?? "";
        const maxTouchPoints = navigator.maxTouchPoints ?? 0;

        return (
            /Android/i.test(userAgent) ||
            /Android/i.test(userAgentDataPlatform) ||
            (/Linux/i.test(platform) && /arm/i.test(platform) && maxTouchPoints > 0)
        );
    };

    const getAudioConstraints = (): boolean | MediaTrackConstraints => {
        if (!enableAndroidAudioConstraints || !getAndroidLikeBrowser()) {
            return true;
        }

        return {
            echoCancellation: { ideal: true },
            noiseSuppression: { ideal: true },
            autoGainControl: { ideal: false }
        };
    };

    const summarizeTrack = (track: MediaStreamTrack): Record<string, unknown> => ({
        id: track.id,
        kind: track.kind,
        label: track.label,
        enabled: track.enabled,
        muted: track.muted,
        readyState: track.readyState
    });

    const attachTrackDiagnostics = (currentStream: MediaStream): void => {
        currentStream.getAudioTracks().forEach((track) => {
            track.onmute = () => {
                trackDiagnosticTrace({
                    enabled: enableDiagnostics,
                    area: "stt",
                    event: "permission:track:mute",
                    properties: {
                        streamId: currentStream.id,
                        streamActive: currentStream.active,
                        track: summarizeTrack(track)
                    }
                });
            };

            track.onunmute = () => {
                trackDiagnosticTrace({
                    enabled: enableDiagnostics,
                    area: "stt",
                    event: "permission:track:unmute",
                    properties: {
                        streamId: currentStream.id,
                        streamActive: currentStream.active,
                        track: summarizeTrack(track)
                    }
                });
            };

            track.onended = () => {
                trackDiagnosticTrace({
                    enabled: enableDiagnostics,
                    area: "stt",
                    event: "permission:track:ended",
                    properties: {
                        streamId: currentStream.id,
                        streamActive: currentStream.active,
                        track: summarizeTrack(track)
                    }
                });
            };
        });
    };

    const createRecorder = (sourceStream = stream): RecordRTCPromisesHandler | undefined => {
        if (!sourceStream) {
            return undefined;
        }

        return new RecordRTCPromisesHandler(sourceStream, {
            type: "audio",
            mimeType: "audio/wav",
            recorderType: RTC.StereoAudioRecorder,
            // timeSlice: 1000,
            disableLogs: true,
            // requires timeSlice above
            ondataavailable: function (blob) {},
            // requires timeSlice above
            onTimeStamp: function (timestamp) {},
            // range 22050 to 96000.
            // sampleRate: 96000,
            // the range 22050 to 96000.
            desiredSampRate: 16000,
            // Legal values are (256, 512, 1024, 2048, 4096, 8192, 16384).
            bufferSize: 16384,
            numberOfAudioChannels: 1
        });
    };

    const getPermissionInitializeRecorder = async (): Promise<MediaStream | undefined> => {
        try {
            trackDiagnosticTrace({
                enabled: enableDiagnostics,
                area: "stt",
                event: "permission:get-user-media:start",
                properties: {
                    androidLike: getAndroidLikeBrowser(),
                    usingAndroidAudioConstraints: enableAndroidAudioConstraints && getAndroidLikeBrowser()
                }
            });

            const st = await navigator.mediaDevices.getUserMedia({
                audio: getAudioConstraints()
            });

            attachTrackDiagnostics(st);

            trackDiagnosticTrace({
                enabled: enableDiagnostics,
                area: "stt",
                event: "permission:get-user-media:success",
                properties: {
                    streamActive: st.active,
                    tracks: st.getAudioTracks().map((track) => ({
                        enabled: track.enabled,
                        muted: track.muted,
                        readyState: track.readyState,
                        label: track.label
                    }))
                }
            });

            setStream(st);
            return st;
        } catch (error) {
            trackDiagnosticException({
                enabled: enableDiagnostics,
                area: "stt",
                event: "permission:get-user-media:error",
                error
            });
        }

        return undefined;
    };

    useEffect(() => {
        void getPermissionInitializeRecorder();
    }, []);

    const resetRecorderPermission = async (): Promise<MediaStream | undefined> => {
        try {
            trackDiagnosticTrace({
                enabled: enableDiagnostics,
                area: "stt",
                event: "permission:reset-recorder:start"
            });

            stream?.getTracks().forEach((track) => track.stop());
        } catch (error) {
            trackDiagnosticException({
                enabled: enableDiagnostics,
                area: "stt",
                event: "permission:reset-recorder:error",
                error
            });
        }

        setStream(undefined);
        const newStream = await getPermissionInitializeRecorder();

        trackDiagnosticTrace({
            enabled: enableDiagnostics,
            area: "stt",
            event: "permission:reset-recorder:complete"
        });

        return newStream;
    };

    return [stream, createRecorder, resetRecorderPermission];
};
